import { PageController } from '@client/components/_core/layout/PageController';
import { type TabItem, Tabs } from '@client/components/_core/navigation/Tabs';
import { useMyEmails } from '@client/components/_shared/email/store';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useUser } from '@client/hooks/business/user/useUser';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { trpc } from '@client/trpc/client';
import { ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG } from '@shared/constants/business/communication';
import { COMPLAINTS_WORKLOAD_FLAG } from '@shared/constants/business/complaint';
import { canReadAnyComplaint } from '@shared/permissions/complaint';
import { hasPermission } from '@shared/permissions/roles';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Badge } from 'antd';
import { Briefcase } from 'lucide-react';
import { memo, useMemo } from 'react';

export const Route = createFileRoute('/console/$orgId/workload-manager')({
    staticData: {
        metadata: {
            title: 'Workload manager',
        },
        ui: {
            widthVariant: 'fullWidth',
            icon: Briefcase,
            title: 'Workload manager',
            subtitle: 'Your assigned work in one place',
            useTitleForChildren: true,
        },
        breadcrumb: {
            inactive: true,
        },
        // CUR-2675: the Accounts role's payment tasks are in My tasks.
        roles: ['user', 'shared_edit_user', 'org_admin', 'accounts'],
        modules: ['claim', 'compliance', 'risk'],
    },
    component: WorkloadManager,
});

const EMAILS_BADGE_OFFSET = [15, 0] as const satisfies [number, number];

const EmailsTabLabel = memo(function EmailsTabLabel({ count }: { count: number | undefined }) {
    // Skip the Badge entirely when there's nothing to show. Mounting the Badge with
    // count=undefined and later transitioning to a number triggers antd's zoom-enter
    // animation (~300ms) on every fresh page load. Rendering the Badge only once we
    // have a positive count means it mounts already visible — motionAppear=false on
    // antd's CSSMotion skips the appear animation, so no initial-load flicker.
    if (!count) return <>Emails</>;
    return (
        <Badge count={count} offset={EMAILS_BADGE_OFFSET} size="small">
            Emails
        </Badge>
    );
});

function WorkloadManager() {
    // CUR-363: the unread badge follows the Emails tab's own "My emails" toggle so it always
    // matches the list (each tab owns its scope control — Tasks has its own "My work" switch).
    const myEmails = useMyEmails();
    const complaintsWorkloadEnabled = useFeatureFlag(COMPLAINTS_WORKLOAD_FLAG, false);
    const emailsEnabled = useFeatureFlag(ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG, false);

    const queryInput = useMemo(() => ({ myEmails: myEmails === true }), [myEmails]);

    // Curium Light (CUR-379, CUR-2685): emails are not part of Light, so a Light org gets no Emails
    // tab and no 15 s unread poll (the server refuses email procedures for Light orgs, so polling
    // would just loop on FORBIDDEN). With the email workload flag off the tab is hidden for every
    // org (the emails route redirects to Tasks), so there is nothing to poll.
    const isLiteOrg = useIsLiteOrg();
    // CUR-2675: each tab needs its own read access, which every role here held until the Accounts role:
    // on its own it reads no complaints, and claim mail only reaches someone with a claim read path the
    // email list follows (`buildReadableEmailClaimFilter`), not its read-only view of every claim.
    const user = useUser();
    const readsComplaints = canReadAnyComplaint(user.roles);
    const readsClaimMail =
        hasPermission(user.roles, 'claim:read') ||
        hasPermission(user.roles, 'claim:read:own_handling_parties') ||
        user.canViewUnallocatedClaims === true;
    const showsEmails = emailsEnabled && !isLiteOrg && readsClaimMail;

    const { data: unreadCount } = trpc.email.countUnreadEmails.useQuery(showsEmails ? queryInput : skipToken, {
        refetchInterval: 1000 * 15,
    });

    const items = useMemo<TabItem[]>(
        () => [
            {
                label: 'Tasks',
                to: '/console/$orgId/workload-manager/tasks',
            },

            ...(complaintsWorkloadEnabled && readsComplaints
                ? [
                      {
                          label: 'Complaints',
                          to: '/console/$orgId/workload-manager/complaints',
                      } satisfies TabItem,
                  ]
                : []),
            ...(showsEmails
                ? [
                      {
                          label: <EmailsTabLabel count={unreadCount?.count} />,
                          to: '/console/$orgId/workload-manager/emails',
                      } satisfies TabItem,
                  ]
                : []),
        ],
        [unreadCount?.count, complaintsWorkloadEnabled, readsComplaints, showsEmails],
    );

    return (
        <PageController route={Route}>
            <Tabs items={items} />
        </PageController>
    );
}
